import { useMemo } from "react";
import type { BlogArticleConfig } from "../types/blog";

type BlogDetailPageProps = {
  data: BlogArticleConfig;
  latestArticles: BlogArticleConfig[];
  onSelectBlog: (slug: string) => void;
  onBack?: () => void;
};

type ContentBlock =
  | { type: "heading"; content: string; level: 2 | 3 }
  | { type: "paragraph"; content: string }
  | { type: "quote"; content: string }
  | { type: "list"; items: string[] };

const parseMarkdownLike = (value: string): ContentBlock[] => {
  const lines = value.replace(/\r\n/g, "\n").split("\n");
  const blocks: ContentBlock[] = [];
  let paragraphBuffer: string[] = [];
  let listBuffer: string[] = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length > 0) {
      blocks.push({ type: "paragraph", content: paragraphBuffer.join(" ") });
      paragraphBuffer = [];
    }
  };

  const flushList = () => {
    if (listBuffer.length > 0) {
      blocks.push({ type: "list", items: [...listBuffer] });
      listBuffer = [];
    }
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      flushList();
      return;
    }

    if (line.startsWith("## ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "heading", level: 2, content: line.slice(3).trim() });
      return;
    }

    if (line.startsWith("### ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "heading", level: 3, content: line.slice(4).trim() });
      return;
    }

    if (line.startsWith("> ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "quote", content: line.slice(2).trim() });
      return;
    }

    if (line.startsWith("- ")) {
      flushParagraph();
      listBuffer.push(line.slice(2).trim());
      return;
    }

    flushList();
    paragraphBuffer.push(line);
  });

  flushParagraph();
  flushList();

  return blocks;
};

const formatDate = (value: string, locale = "en-US") => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};

export default function BlogDetailPage({
  data,
  latestArticles,
  onSelectBlog,
  onBack,
}: BlogDetailPageProps) {
  const blocks = useMemo(() => parseMarkdownLike(data.detail.bodyMarkdown), [data.detail.bodyMarkdown]);
  const contentId = `blog-content-${data.id}`;
  const formattedDate = formatDate(data.basic.publishedAt, data.locale || "en-US");

  return (
    <div className="bg-white pb-20 text-[#101722]">
      <section className="bg-[#f4f6fa] px-4 pb-20 pt-20 md:px-8 lg:px-10 lg:pt-24">
        <div className="mx-auto flex w-full max-w-[1180px] flex-col items-center text-center">
          <div className="max-w-4xl">
            <h1 className="text-4xl font-black leading-tight text-[#070b14] md:text-6xl">{data.basic.title}</h1>
            <p className="mt-6 text-sm tracking-[0.2em] text-[#7d8696] md:text-base">
              {formattedDate}
            </p>
            <a
              href={`#${contentId}`}
              className="mt-10 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#f7b500] text-xl font-semibold text-white shadow-[0_16px_30px_rgba(247,181,0,0.28)]"
              aria-label="Scroll to article content"
            >
              ↓
            </a>
          </div>

          <div className="mt-16 w-full overflow-hidden rounded-[22px] bg-white shadow-[0_24px_70px_rgba(15,23,36,0.12)]">
            <img
              src={data.detail.heroImageUrl || data.basic.coverImageUrl}
              alt={data.basic.title}
              className="h-[320px] w-full object-cover md:h-[540px]"
            />
          </div>
        </div>
      </section>

      <section id={contentId} className="px-4 pt-14 md:px-8 lg:px-10">
        <div className="mx-auto w-full max-w-[1080px]">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#dbe4ee] px-4 py-2 text-sm font-semibold text-[#48607c] transition hover:border-[#1d4ed8] hover:text-[#1d4ed8]"
            >
              ← Back to blog list
            </button>
          )}

          <div className="space-y-6 text-[17px] leading-8 text-[#6b7280] md:text-[18px]">
            {blocks.map((block, index) => {
              if (block.type === "heading") {
                return block.level === 2 ? (
                  <h2 key={`heading-${index}`} className="pt-3 text-3xl font-bold leading-tight text-[#0f172a]">
                    {block.content}
                  </h2>
                ) : (
                  <h3 key={`heading-${index}`} className="pt-2 text-2xl font-semibold leading-tight text-[#0f172a]">
                    {block.content}
                  </h3>
                );
              }

              if (block.type === "quote") {
                return (
                  <blockquote
                    key={`quote-${index}`}
                    className="border-l-4 border-[#f7b500] pl-5 text-xl leading-9 text-[#4b5563]"
                  >
                    {block.content}
                  </blockquote>
                );
              }

              if (block.type === "list") {
                return (
                  <ul key={`list-${index}`} className="space-y-3 pl-1">
                    {block.items.map((item) => (
                      <li key={item} className="flex gap-3">
                        <span className="mt-2 h-2 w-2 rounded-full bg-[#f7b500]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                );
              }

              return <p key={`paragraph-${index}`}>{block.content}</p>;
            })}
          </div>

        </div>
      </section>

      {data.detail.latestSection.enabled && latestArticles.length > 0 && (
        <section className="px-4 pt-20 md:px-8 lg:px-10">
          <div className="mx-auto w-full max-w-[1080px]">
            <div className="text-center">
              <p className="text-lg text-[#f7b500]">{data.detail.latestSection.eyebrow}</p>
              <h2 className="mt-3 text-4xl font-black text-[#070b14] md:text-5xl">
                {data.detail.latestSection.title}
              </h2>
            </div>

            <div className="mt-12 grid gap-8 md:grid-cols-2 xl:grid-cols-3">
              {latestArticles.map((article) => (
                <article key={article.id} className="group">
                  <div className="overflow-hidden rounded-[18px] bg-[#f5f7fb] shadow-[0_18px_44px_rgba(15,23,36,0.08)]">
                    <img
                      src={article.basic.coverImageUrl}
                      alt={article.basic.title}
                      className="h-[230px] w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="mt-4">
                    <span className="inline-flex rounded-md bg-[#16336d] px-3 py-1 text-xs font-semibold text-white">
                      {formatDate(article.basic.publishedAt, article.locale || "en-US")}
                    </span>
                    <h3 className="mt-4 text-2xl font-black leading-tight text-[#0f172a]">{article.basic.title}</h3>
                    <button
                      type="button"
                      onClick={() => onSelectBlog(article.slug)}
                      className="mt-5 rounded-md bg-[#f7b500] px-5 py-3 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(247,181,0,0.28)] transition hover:-translate-y-0.5"
                    >
                      Read More
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
